import React, { useCallback, useEffect, useState } from "react";
import {
  FaFilter,
  FaSpinner,
  FaInbox,
  FaExclamationCircle,
  FaEye,
  FaCheckCircle,
  FaClock,
  FaHourglassHalf,
  FaClipboardList,
} from "react-icons/fa";
import { dailyActivityApi } from "./dailyActivityApi";
import { mapApiTaskToFrontend } from "./dailyActivityMappers";
import {
  PRIORITY_STYLES,
  STATUS_STYLES,
  CMS_PRIMARY,
} from "./DailyActivityLoggerConstants";
import { formatLogDate, todayIsoDate } from "./DailyActivityLoggerUtils";
import TaskModalRoot from "../TaskModalRoot";
import { ModalFooterButton } from "./taskModalUi";

const fieldBase =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-800 shadow-sm outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/15";

function TaskHistoryPanel({ isActive = true }) {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(todayIsoDate());
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportTasks, setReportTasks] = useState([]);
  const [tomorrowPlanTasks, setTomorrowPlanTasks] = useState([]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dailyActivityApi.getHistory({
        from: fromDate,
        to: toDate,
      });
      const list = res?.data?.data?.reports || [];
      setReports(list);
    } catch (err) {
      setReports([]);
      setError(err?.response?.data?.message || "Could not load history.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    if (!isActive) return;
    fetchHistory();
  }, [isActive, fetchHistory]);

  const openReport = async (report) => {
    setSelectedReport(report);
    setViewOpen(true);
    setViewLoading(true);
    setReportTasks([]);
    setTomorrowPlanTasks([]);
    try {
      const res = await dailyActivityApi.getReportTasks(report.id);
      const tasks = (res?.data?.data?.tasks || []).map(mapApiTaskToFrontend);
      const tomorrow = (res?.data?.data?.tomorrow_plan || []).map(mapApiTaskToFrontend);
      setReportTasks(tasks);
      setTomorrowPlanTasks(tomorrow);
      if (res?.data?.data?.report) {
        setSelectedReport((prev) => ({ ...prev, ...res.data.data.report }));
      }
    } catch (err) {
      setReportTasks([]);
      setTomorrowPlanTasks([]);
    } finally {
      setViewLoading(false);
    }
  };

  const closeReport = () => {
    setViewOpen(false);
    setSelectedReport(null);
    setReportTasks([]);
    setTomorrowPlanTasks([]);
  };

  const formatSentTime = (value) => {
    if (!value) return null;
    const [datePart, timePart] = value.split(" ");
    return {
      date: formatLogDate(datePart),
      time: timePart?.slice(0, 5) || null,
    };
  };

  const renderTaskTable = (tasks, emptyMessage) => {
    if (tasks.length === 0) {
      return (
        <div className="flex flex-col items-center gap-2 py-10 text-gray-400">
          <FaInbox size={18} />
          <p className="text-xs">{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="dal-history-report-table-wrap overflow-x-auto rounded-xl border border-gray-100">
        <table className="dal-history-report-table w-full min-w-[640px] text-left text-xs">
          <thead className="bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2.5">Task</th>
              <th className="px-3 py-2.5">Priority</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Progress</th>
              <th className="min-w-[260px] px-3 py-2.5">Notes</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr
                key={task.id}
                className="border-b border-gray-50 align-top hover:bg-blue-50/40"
              >
                <td className="px-3 py-3 font-medium text-gray-800">
                  {task.description || "—"}
                </td>
                <td className="px-3 py-3">
                  {task.priority ? (
                    <span
                      className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                        PRIORITY_STYLES[task.priority] || ""
                      }`}
                    >
                      {task.priority}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-3 py-3">
                  <span
                    className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                      STATUS_STYLES[task.status] || ""
                    }`}
                  >
                    {task.status || "Pending"}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-[#007BFF]"
                        style={{
                          width: `${task.progress_percent || 0}%`,
                        }}
                      />
                    </div>
                    <span className="text-[11px] font-semibold text-gray-500">
                      {task.progress_percent || 0}%
                    </span>
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-600">
                  {task.outcome?.trim() ? (
                    <div className="dal-history-report-notes whitespace-pre-wrap break-words leading-relaxed">
                      {task.outcome}
                    </div>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 bg-gray-50/70 px-4 py-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[130px]">
              <label className="mb-1 block text-[10px] font-semibold uppercase text-gray-400">
                From
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={fieldBase}
              />
            </div>
            <div className="min-w-[130px]">
              <label className="mb-1 block text-[10px] font-semibold uppercase text-gray-400">
                To
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={fieldBase}
              />
            </div>
            <button
              type="button"
              onClick={fetchHistory}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold text-white shadow-sm disabled:opacity-50"
              style={{ backgroundColor: CMS_PRIMARY }}
            >
              {loading ? (
                <FaSpinner className="animate-spin" size={11} />
              ) : (
                <FaFilter size={10} />
              )}
              Apply
            </button>
          </div>
        </div>

        <div className="px-4 py-4">
          {loading ? (
            <div className="flex flex-col items-center gap-2 py-12 text-gray-400">
              <FaSpinner
                className="animate-spin"
                size={18}
                style={{ color: CMS_PRIMARY }}
              />
              <p className="text-xs">Loading reports…</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 py-12 text-red-500">
              <FaExclamationCircle size={18} />
              <p className="text-xs">{error}</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-gray-400">
              <FaInbox size={18} />
              <p className="text-xs">No reports found for the selected dates.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map((report) => {
                const sent = formatSentTime(report.sent_at);
                const planSent = formatSentTime(report.plan_sent_at);

                return (
                  <div
                    key={report.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm transition hover:border-blue-100 hover:shadow"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#35314d]">
                        {formatLogDate(report.report_date)}
                      </p>
                      <p className="mt-0.5 text-[11px] text-gray-400">
                        Today report sent {sent?.date || "—"}
                        {sent?.time && <span className="ml-1">at {sent.time}</span>}
                      </p>
                      {report.has_tomorrow_plan ? (
                        <p className="mt-1 flex items-center gap-1 text-[11px] text-violet-600">
                          <FaClipboardList size={10} />
                          Tomorrow plan: {report.tomorrow_task_count || 0} task
                          {(report.tomorrow_task_count || 0) === 1 ? "" : "s"}
                          {planSent?.time && (
                            <span className="text-violet-500">· sent at {planSent.time}</span>
                          )}
                        </p>
                      ) : (
                        <p className="mt-1 text-[11px] text-gray-400">
                          No tomorrow plan submitted
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-400">Total</span>
                        <span className="font-bold text-gray-700">
                          {report.total_tasks}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FaCheckCircle className="text-emerald-500" size={11} />
                        <span className="font-semibold text-emerald-600">
                          {report.completed}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FaClock className="text-blue-500" size={11} />
                        <span className="font-semibold text-blue-600">
                          {report.in_progress}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FaHourglassHalf className="text-amber-500" size={11} />
                        <span className="font-semibold text-amber-600">
                          {report.not_started}
                        </span>
                      </div>
                      <div className="hidden sm:flex items-center gap-1.5">
                        <span className="text-gray-400">Avg</span>
                        <span className="font-semibold text-gray-700">
                          {Math.round(report.avg_progress)}%
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => openReport(report)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:border-[#007BFF] hover:text-[#007BFF]"
                    >
                      <FaEye size={11} />
                      View Report
                    </button>
                  </div>
                );
              })}

              <p className="mt-2 text-[11px] text-gray-400">
                {reports.length} report{reports.length !== 1 ? "s" : ""}
              </p>
            </div>
          )}
        </div>
      </div>

      <TaskModalRoot open={viewOpen} onBackdropClick={closeReport}>
        <div
          className="relative z-10 flex w-full max-w-5xl max-h-[min(90vh,820px)] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dal-history-report-title"
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#007BFF]">
                Task history
              </p>
              <h3
                id="dal-history-report-title"
                className="mt-0.5 text-lg font-bold text-[#35314d]"
              >
                Report – {selectedReport ? formatLogDate(selectedReport.report_date) : ""}
              </h3>
              {selectedReport && (
                <p className="mt-1 text-xs text-gray-500">
                  {selectedReport.total_tasks} today tasks · {selectedReport.completed}{" "}
                  completed · {selectedReport.in_progress} in progress ·{" "}
                  {selectedReport.not_started} not started
                  {selectedReport.has_tomorrow_plan && (
                    <span className="text-violet-600">
                      {" "}
                      · {selectedReport.tomorrow_task_count || tomorrowPlanTasks.length}{" "}
                      tomorrow planned
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {viewLoading ? (
              <div className="flex flex-col items-center gap-2 py-14 text-gray-400">
                <FaSpinner
                  className="animate-spin"
                  size={18}
                  style={{ color: CMS_PRIMARY }}
                />
                <p className="text-xs">Loading report…</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Today&apos;s tasks
                  </h4>
                  {renderTaskTable(reportTasks, "No today tasks found for this report.")}
                </div>

                <div>
                  <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-violet-600">
                    <FaClipboardList size={11} />
                    Tomorrow&apos;s plan
                    {selectedReport?.plan_sent_at && (
                      <span className="font-normal normal-case text-gray-400">
                        · submitted{" "}
                        {formatSentTime(selectedReport.plan_sent_at)?.time || ""}
                      </span>
                    )}
                  </h4>
                  {renderTaskTable(
                    tomorrowPlanTasks,
                    selectedReport?.has_tomorrow_plan
                      ? "Tomorrow plan was submitted but tasks are no longer available."
                      : "No tomorrow plan was submitted with this report."
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex shrink-0 justify-end border-t border-gray-100 px-5 py-4">
            <ModalFooterButton primary onClick={closeReport}>
              Close
            </ModalFooterButton>
          </div>
        </div>
      </TaskModalRoot>
    </>
  );
}

export default TaskHistoryPanel;
